import React, { useState, useEffect, useCallback, useRef, Suspense } from 'react';
import { HashRouter, Routes, Route, useNavigate } from 'react-router-dom';
import Header from './components/Layout/Header';
import InvoiceForm from './components/Invoice/InvoiceForm';
import InvoiceTemplate from './components/Invoice/InvoiceTemplate';
import Footer from './components/Layout/Footer';
import VoiceAssistant from './components/AI/VoiceAssistant';
import HistorySidebar from './components/History/HistorySidebar';
import type { InvoiceData, InvoiceSettings, SavedInvoice } from './types';
import { DEFAULT_SETTINGS, EMPTY_INVOICE, DUMMY_INVOICE, CURRENCY_MAP } from './constants';
import {
  saveDraft,
  loadDraft,
  saveToHistory,
  loadHistory,
  clearHistory,
} from './services/storageService';
import { CheckCircle2, Download, Printer, Eye } from 'lucide-react';

const AVAILABLE_LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
  { code: 'de', label: 'Deutsch' },
  { code: 'ur', label: 'اردو' },
];

// Lazy-loaded pages
const LandingPage = React.lazy(() => import('./components/Landing/LandingPage'));
const NotFound = React.lazy(() => import('./pages/NotFound'));
const Security = React.lazy(() => import('./pages/Security'));
const Privacy = React.lazy(() => import('./pages/Privacy'));
const AboutUs = React.lazy(() => import('./pages/AboutUs'));
const ContactUs = React.lazy(() => import('./pages/ContactUs'));
const TermsConditions = React.lazy(() => import('./pages/TermsConditions'));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-white dark:bg-slate-950">
    <div className="w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full animate-spin"></div>
  </div>
);

const EditorLayout: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<InvoiceData>(DUMMY_INVOICE);
  const [settings, setSettings] = useState<InvoiceSettings>(DEFAULT_SETTINGS);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [history, setHistory] = useState<SavedInvoice[]>([]);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [showLangPicker, setShowLangPicker] = useState(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const draft = loadDraft();
    const localHistory = loadHistory();
    setHistory(localHistory);
    if (draft) setData(draft);
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      setIsDarkMode(true);
      document.documentElement.classList.add('dark');
    }
    setLoaded(true);
  }, []);

  // Debounced auto-save (500ms delay)
  useEffect(() => {
    if (!loaded || !settings.autoSave) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveDraft(data);
    }, 500);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [data, loaded, settings.autoSave]);

  const updateData = useCallback((updates: Partial<InvoiceData>) => {
    setData((prev) => ({ ...prev, ...updates }));
  }, []);

  const handleVoiceUpdate = useCallback(
    (
      partial: Partial<InvoiceData> & {
        addItem?: { description?: string; quantity?: number; rate?: number };
      }
    ) => {
      setData((prev) => {
        const { addItem, ...rest } = partial;
        const nextData = { ...prev, ...rest };
        if (addItem) {
          nextData.items = [
            ...prev.items,
            {
              id: crypto.randomUUID(),
              description: addItem.description || 'New Service',
              quantity: addItem.quantity || 1,
              rate: addItem.rate || 0,
              tax: 0,
            },
          ];
        }
        return nextData;
      });
    },
    []
  );

  const handleManualSave = () => {
    saveDraft(data);
    const updatedHistory = saveToHistory(data);
    setHistory(updatedHistory);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const handleExport = () => {
    handleManualSave();
    window.print();
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F8FAFC] dark:bg-slate-950">
      <Header
        language={settings.language}
        isDarkMode={isDarkMode}
        onNavigateLanding={() => navigate('/')}
        onOpenLangPicker={() => setShowLangPicker(true)}
        onNewInvoice={() => {
          if (confirm('Create new empty invoice?')) setData(EMPTY_INVOICE);
        }}
        onOpenHistory={() => setIsHistoryOpen(true)}
        onDownload={handleExport}
        onToggleDarkMode={() => {
          setIsDarkMode(!isDarkMode);
          document.documentElement.classList.toggle('dark');
        }}
      />

      {showToast && (
        <div className="fixed top-24 left-1/2 -translate-x-1/2 z-[200] animate-in slide-in-from-top-4">
          <div className="bg-slate-900 text-white px-6 py-3 rounded-2xl shadow-2xl flex items-center gap-3 border border-slate-800">
            <CheckCircle2 className="text-emerald-500" size={18} />
            <span className="text-xs font-black uppercase tracking-widest text-white">
              Document Vault Synced
            </span>
          </div>
        </div>
      )}

      {/* Language Picker Modal */}
      {showLangPicker && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/50 backdrop-blur-sm no-print" onClick={() => setShowLangPicker(false)}>
          <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-800 p-8 w-80" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-black text-xl text-slate-900 dark:text-white mb-6 tracking-tight">Select Language</h3>
            <div className="space-y-2">
              {AVAILABLE_LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => {
                    setSettings({ ...settings, language: lang.code });
                    setShowLangPicker(false);
                  }}
                  className={`w-full text-left px-4 py-3 rounded-xl font-bold text-sm transition-all ${
                    settings.language === lang.code
                      ? 'bg-brand-500 text-white'
                      : 'bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-brand-50 dark:hover:bg-slate-700'
                  }`}
                >
                  {lang.label} ({lang.code.toUpperCase()})
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <main className="flex-1 max-w-7xl w-full mx-auto p-6 lg:p-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5 space-y-10 no-print">
            <div className="flex items-center justify-between">
              <h2 className="font-black text-4xl text-slate-900 dark:text-white tracking-tighter">
                Console
              </h2>
              <div className="flex gap-2">
                <button
                  onClick={handleManualSave}
                  className="p-2.5 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 text-slate-400 hover:text-brand-500 hover:border-brand-500 transition-all"
                >
                  <CheckCircle2 size={18} />
                </button>
              </div>
            </div>

            <div className="bg-white dark:bg-slate-900 p-8 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-xl grid grid-cols-2 gap-8">
              <div className="space-y-3">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">
                  Visual Template
                </label>
                <select
                  className="w-full text-sm bg-slate-50 dark:bg-slate-950 p-4 rounded-2xl font-black outline-none border border-slate-200 dark:border-slate-800 focus:border-brand-500 transition-colors cursor-pointer"
                  value={settings.template}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      template: e.target.value as InvoiceSettings['template'],
                    })
                  }
                >
                  <option value="modern">Modern Professional</option>
                  <option value="bold">Bold Impact</option>
                  <option value="spreadsheet">Legacy Spreadsheet</option>
                  <option value="standard">Standard Business</option>
                </select>
              </div>
              <div className="space-y-3">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">
                  Currency
                </label>
                <select
                  className="w-full text-sm bg-slate-50 dark:bg-slate-950 p-4 rounded-2xl font-black outline-none border border-slate-200 dark:border-slate-800 focus:border-brand-500 transition-colors cursor-pointer"
                  value={settings.currency}
                  onChange={(e) => {
                    const curr = e.target.value;
                    setSettings({
                      ...settings,
                      currency: curr,
                      currencySymbol: CURRENCY_MAP[curr] || '$',
                    });
                  }}
                >
                  <option value="USD">USD ($)</option>
                  <option value="EUR">EUR (€)</option>
                  <option value="GBP">GBP (£)</option>
                  <option value="INR">INR (₹)</option>
                  <option value="JPY">JPY (¥)</option>
                  <option value="AUD">AUD (A$)</option>
                  <option value="CAD">CAD (C$)</option>
                  <option value="CHF">CHF</option>
                  <option value="PKR">PKR (₨)</option>
                  <option value="AED">AED (د.إ)</option>
                </select>
              </div>
            </div>

            <InvoiceForm
              data={data}
              settings={settings}
              onUpdate={updateData}
              onSettingsChange={setSettings}
              onManualSave={handleManualSave}
            />
          </div>

          <div className="lg:col-span-7 relative">
            <div className="lg:sticky lg:top-32">
              <div className="mb-8 flex items-center justify-between no-print px-4">
                <div className="flex items-center gap-4">
                  <h2 className="font-black text-4xl text-slate-900 dark:text-white tracking-tighter">
                    Document
                  </h2>
                  <div className="flex items-center gap-2 bg-emerald-50 dark:bg-emerald-950/30 px-3 py-1.5 rounded-full border border-emerald-100 dark:border-emerald-800">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></div>
                    <span className="text-[9px] font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-widest leading-none">
                      Synthesis Active
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={handleExport}
                    className="p-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl text-slate-500 hover:text-brand-500 transition-all shadow-sm flex items-center gap-2 text-xs font-black uppercase tracking-widest"
                  >
                    <Download size={16} /> Export
                  </button>
                  <button
                    onClick={handlePrint}
                    className="p-3 bg-slate-900 dark:bg-brand-500 text-white dark:text-slate-950 rounded-2xl hover:scale-105 transition-all shadow-lg flex items-center gap-2 text-xs font-black uppercase tracking-widest"
                  >
                    <Printer size={16} /> Print
                  </button>
                </div>
              </div>

              <div className="relative group">
                <div className="absolute -inset-2 bg-gradient-to-b from-brand-500/10 to-transparent dark:from-brand-900/10 opacity-40 rounded-[3.5rem] blur-2xl group-hover:opacity-60 transition-opacity"></div>
                <div className="relative shadow-[0_60px_100px_-20px_rgba(0,0,0,0.12)] dark:shadow-none rounded-[3.5rem] overflow-hidden bg-white border border-slate-200 dark:border-slate-800 transition-all transform hover:scale-[1.002]">
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-brand-500 to-blue-600 z-50"></div>
                  <InvoiceTemplate data={data} settings={settings} />
                </div>
              </div>

              <div className="mt-8 no-print flex items-center justify-between px-6">
                <div className="flex items-center gap-3 text-slate-300 dark:text-slate-700">
                  <Eye size={14} />
                  <span className="text-[10px] font-black uppercase tracking-[0.2em]">
                    Live Vector Rendering
                  </span>
                </div>
                <div className="flex items-center gap-2 text-slate-300 dark:text-slate-700">
                  <span className="text-[10px] font-black uppercase tracking-[0.2em]">
                    Scale 1:1
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <HistorySidebar
        isOpen={isHistoryOpen}
        onClose={() => setIsHistoryOpen(false)}
        history={history}
        onLoad={setData}
        onClear={() => {
          clearHistory();
          setHistory([]);
        }}
      />
      <VoiceAssistant onUpdateInvoice={handleVoiceUpdate} />
      <Footer />
    </div>
  );
};

const AppRoutes: React.FC = () => {
  const navigate = useNavigate();
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<LandingPage onStart={() => navigate('/editor')} />} />
        <Route path="/editor" element={<EditorLayout />} />
        <Route path="/security" element={<Security />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/about" element={<AboutUs />} />
        <Route path="/contact" element={<ContactUs />} />
        <Route path="/terms" element={<TermsConditions />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

const App: React.FC = () => {
  return (
    <HashRouter>
      <AppRoutes />
    </HashRouter>
  );
};

export default App;
